import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Tabs,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { Change_Supplier_BusinessStatus } from "apollo/mutuations";
import { GET_SME_SUPPLIERS_BUSINESSES } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import AdminUpdateSupplierBusiness from "components/SupplierBusiness/AdminUpdateSupplierBussiness";
import { customLoader } from "components/utilities/loader";
import { useState } from "react";
import { FiEye } from "react-icons/fi";
import SupplierFilter from "../transactions/supplier";
import SupplierDetailModal from "./detail";

const SmeSupplierBusinesses = () => {
  const [size, setSize] = useState("10");
  const [searchValue, setSearchValue] = useState("");
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [activeTab, setActiveTab] = useState("first");
  const [user, setUser] = useState(null);
  const [openedView, setOpenedView] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [openedStatusChange, setOpenedStatusChange] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessStatus, setBusinessStatus] = useState(null);

  const { data, loading, refetch } = useQuery(GET_SME_SUPPLIERS_BUSINESSES, {
    variables: {
      search: searchValue,
      supplier_id: user?.value,
      status:
        activeTab === "first"
          ? "ACTIVE"
          : activeTab === "second"
            ? "PENDING"
            : "SUSPENDED",
      first: parseInt(size, 10),
      page: activePage,
      ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to get SME supplier businesses",
      });
    },
  });

  const [changeBusinessStatus] = useMutation(Change_Supplier_BusinessStatus, {
    onCompleted(result) {
      const action =
        result.changeSupplierBusinessStatus.status === "ACTIVE"
          ? "Activated"
          : "Deactivated";
      showNotification({
        color: "green",
        title: "Success",
        message: `Supplier Business ${action} successfully`,
      });
      setOpenedStatusChange(false);
      refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || error.message,
      });
    },
  });

  const handleStatus = (business, nextStatus) => {
    setSelectedBusiness(business);
    setBusinessStatus(nextStatus);
    setOpenedStatusChange(true);
  };

  const handleStatusChange = () => {
    changeBusinessStatus({
      variables: { id: selectedBusiness.id, status: businessStatus },
    });
  };

  const theme = useMantineTheme();

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
    setActivePage(1);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
    setActivePage(1);
  };

  const headerData = [
    {
      label: "Name",
      key: "business_name",
      sortable: true,
      searchable: true,
      width: "20%",
      render: (rowData) => <span>{rowData.business_name}</span>,
    },
    {
      label: "Email",
      key: "business_email",
      sortable: false,
      searchable: true,
      render: (rowData) => <span>{rowData.business_email}</span>,
    },
    {
      label: "Phone",
      key: "business_phone_number",
      sortable: false,
      searchable: true,
      render: (rowData) => <span>{rowData.business_phone_number}</span>,
    },
    {
      label: "Warehouse",
      key: "number_of_warehouses",
      sortable: false,
      width: "8%",
      searchable: true,
      render: (rowData) => (
        <span style={{ display: "block", width: "30px" }}>
          {rowData.number_of_warehouses}
        </span>
      ),
    },
    {
      label: "Tin",
      key: "tin",
      sortable: false,
      width: "10%",
      searchable: true,
      render: (rowData) => <span>{rowData.tin}</span>,
    },
    {
      label: "Status",
      key: "account",
      width: "8%",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.status === "ACTIVE" ? (
              <Badge variant="light" color="green">
                Active
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Not Active
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <ActionButton
            label="Edit"
            onClick={() => {
              setSelectedBusiness(rowData);
              setOpenedEdit(true);
            }}
          >
            <EditIcon style={{ fontSize: "1rem" }} />
          </ActionButton>
          <ActionButton
            label="View"
            color="success"
            onClick={() => {
              setSelectedBusiness(rowData);
              setOpenedView(true);
            }}
          >
            <FiEye style={{ fontSize: "1rem" }} />
          </ActionButton>
          <ActionButton
            label={rowData.status === "ACTIVE" ? "Deactivate" : "Activate"}
            color={rowData.status === "ACTIVE" ? "deactivate" : "activate"}
            onClick={() =>
              handleStatus(
                rowData,
                rowData.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE",
              )
            }
          >
            {rowData.status === "ACTIVE" ? (
              <PersonOffIcon style={{ fontSize: "1rem" }} />
            ) : (
              <HowToRegIcon style={{ fontSize: "1rem" }} />
            )}
          </ActionButton>
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <Tabs
        color="#FF6A00"
        value={activeTab}
        onTabChange={(tab) => {
          if (tab) {
            setActiveTab(tab);
            setActivePage(1);
          }
        }}
        styles={{
          tabList: {
            borderBottom: "2px solid #FF6A00",
          },
          tab: {
            "&[data-active]": {
              borderColor: "#FF6A00",
            },
            "&:hover": {
              borderColor: "#FF6A00",
            },
          },
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="first">
            <span style={{ color: "#666666", fontWeight: "bold" }}>Active</span>
          </Tabs.Tab>
          <Tabs.Tab value="second">
            <span style={{ color: "#666666", fontWeight: "bold" }}>
              Pending
            </span>
          </Tabs.Tab>
          <Tabs.Tab value="third">
            <span style={{ color: "#666666", fontWeight: "bold" }}>
              Suspended
            </span>
          </Tabs.Tab>
        </Tabs.List>
        <Modal
          opened={openedStatusChange}
          onClose={() => setOpenedStatusChange(false)}
          title="Warning"
          centered
        >
          <p>{`Are you sure you want to ${
            businessStatus === "ACTIVE" ? "activate" : "deactivate"
          } this supplier business?`}</p>
          <Group position="right">
            <Button onClick={handleStatusChange} color="red">
              {businessStatus === "ACTIVE" ? "Activate" : "Deactivate"}
            </Button>
          </Group>
        </Modal>
        <Drawer
          opened={openedEdit}
          padding="xl"
          title="Update Supplier Business"
          size="80%"
          position="bottom"
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          onClose={() => setOpenedEdit(false)}
        >
          <AdminUpdateSupplierBusiness
            setOpened={setOpenedEdit}
            data={selectedBusiness}
          />
        </Drawer>
        <Drawer
          opened={openedView}
          padding="xl"
          title="Supplier Business Details"
          size="80%"
          position="bottom"
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          onClose={() => setOpenedView(false)}
        >
          <SupplierDetailModal data={selectedBusiness} />
        </Drawer>
        <Card shadow="sm" p="lg">
          <ScrollArea>
            {loading && (
              <LoadingOverlay
                visible={loading}
                color="blue"
                overlayBlur={2}
                loader={customLoader}
              />
            )}
            <B2bTable
              activePage={activePage}
              handleChange={setActivePage}
              paginatorInfo={data?.smeSuppliersBusinesses?.paginatorInfo}
              header={headerData}
              clearInput={clearInput}
              handelSearch={handleManualSearch}
              searchValue={confirmedSearch}
              onSearchChange={setConfirmedSearch}
              loading={loading}
              data={data?.smeSuppliersBusinesses?.data || []}
              size={size}
              handlePageSizeChange={handlePageSizeChange}
              layout={{ minWidth: 700 }}
              filterData={() => (
                <SupplierFilter
                  user={user}
                  onCardClick={setUser}
                  isSme={true}
                  userType="supplier"
                />
              )}
            />
          </ScrollArea>
        </Card>
      </Tabs>
    </div>
  );
};

export default SmeSupplierBusinesses;
